import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { createApiUrl } from '../utils/apiConfig';
import './SchoolAdminDetail.css';

const SchoolAdminDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    contactName: '',
    email: ''
  });
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchSchoolAdmin = async () => {
      try {
        setLoading(true);
        const response = await axios.get(createApiUrl(`/api/school-admin/${id}`), {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        });
        setAdmin(response.data);
        // Initialize edit form with current values
        setEditForm({
          contactName: response.data.contactName || '',
          email: response.data.email || ''
        });
      } catch (error) {
        console.error('Error fetching school admin:', error);
        setError('Failed to load school admin details');
      } finally {
        setLoading(false);
      }
    };

    const checkAdminStatus = async () => {
      try {
        const response = await axios.get(createApiUrl('/api/dashboard'), { withCredentials: true });
        
        // Check for both isAdmin and role === 'admin'
        if (response.data && response.data.user && (response.data.user.isAdmin || response.data.user.role === 'admin')) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    fetchSchoolAdmin();
    checkAdminStatus();
  }, [id]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate('/admin-dashboard')} variant="primary">
          Back to Admin Dashboard
        </Button>
      </div>
    );
  }

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    setEditForm({
      contactName: admin.contactName || '',
      email: admin.email || ''
    });
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(createApiUrl(`/api/school-admin/${id}`), {
        contactName: editForm.contactName,
        email: editForm.email
      }, { withCredentials: true });
      
      setAdmin(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating school admin:', error);
      alert('Failed to update school admin');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!admin) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">School Admin not found</Alert>
        <Button onClick={() => navigate('/admin-dashboard')} variant="primary">
          Back to Admin Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="school-admin-detail-container">
      <div className="container">
        <div className="school-admin-detail-main-content">
          <div className="school-admin-detail-header">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="page-title">School Admin Details</h2>
              <Button onClick={() => navigate('/admin-dashboard')} className="btn-back">
                Back to Admin Dashboard
              </Button>
            </div>
          </div>

          <Row>
            <Col md={6}>
              <Card className="school-admin-detail-card mb-4">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4>Personal Information</h4>
                    {isAdmin && (
                      <div>
                        {!isEditing ? (
                          <Button 
                            onClick={handleEdit}
                            variant="primary"
                            size="sm"
                          >
                            Edit
                          </Button>
                        ) : (
                          <div>
                            <Button 
                              onClick={handleSave}
                              variant="success"
                              size="sm"
                              className="me-2"
                            >
                              Save
                            </Button>
                            <Button 
                              onClick={handleCancel}
                              variant="secondary"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>Name:</strong>
                        {isEditing ? (
                          <input
                            type="text"
                            name="contactName"
                            value={editForm.contactName}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        ) : (
                          <p>{admin.contactName}</p>
                        )}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>Email:</strong>
                        {isEditing ? (
                          <input
                            type="email"
                            name="email"
                            value={editForm.email}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        ) : (
                          <p>{admin.email}</p>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>Username:</strong>
                        <p>{admin.username}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>Status:</strong>
                        <p><span className={`badge bg-${admin.isSchAdmin ? 'success' : 'warning'}`}>
                          {admin.isSchAdmin ? 'Active' : 'Inactive'}
                        </span></p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="school-admin-detail-card mb-4">
                <Card.Header>
                  <h4>School Information</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>School Name:</strong>
                        <p>{admin.schoolName}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>School Code:</strong>
                        <p>{admin.schoolCode}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>Organization Code:</strong>
                        <p>{admin.orgCode || 'Not assigned'}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="school-admin-detail-info">
                        <strong>Teacher Code:</strong>
                        <p>{admin.tchCode || 'Not assigned'}</p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="school-admin-detail-card mb-4">
            <Card.Header>
              <h4>Address Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col sm={6}>
                  <div className="school-admin-detail-info">
                    <strong>Address:</strong>
                    <p>{admin.address || 'Not provided'}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="school-admin-detail-info">
                    <strong>Post/Zip Code:</strong>
                    <p>{admin.postZipCode || 'Not provided'}</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="school-admin-detail-card mb-4">
            <Card.Header>
              <h4>Subscriptions</h4>
            </Card.Header>
            <Card.Body>
              {admin.subscriptions && admin.subscriptions.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Quantity</th>
                        <th>Remaining</th>
                        <th>Starts</th>
                        <th>Expires</th>
                        <th>Subscription ID</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admin.subscriptions.map((subscription, index) => (
                        <tr key={subscription._id || index}>
                          <td>
                            <strong>{subscription.plan?.name || subscription.planId?.name || subscription.planName || 'Unknown Plan'}</strong>
                            {subscription.isTrial && <span className="badge bg-info ms-2">Trial</span>}
                          </td>
                          <td>{subscription.quantity || subscription.studentCount || 'N/A'}</td>
                          <td>
                            {subscription.remaining ? subscription.remaining : 
                             subscription.quantity ? subscription.quantity - (subscription.used || 0) : 'N/A'}
                          </td>
                          <td>{subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'Not set'}</td>
                          <td>{subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'Not set'}</td>
                          <td>
                            <code className="text-muted">{subscription._id || 'N/A'}</code>
                          </td>
                          <td>
                            <span className={`badge bg-${subscription.status === 'active' ? 'success' : subscription.status === 'expired' ? 'danger' : 'warning'}`}>
                              {subscription.status || 'Unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted">
                  <p>No subscriptions found</p>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="school-admin-detail-card mb-4">
            <Card.Header>
              <h4>Account Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col sm={6}>
                  <div className="school-admin-detail-info">
                    <strong>Created:</strong>
                    <p>{new Date(admin.createdAt).toLocaleDateString()}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="school-admin-detail-info">
                    <strong>Stripe Customer ID:</strong>
                    <p>{admin.stripeCustomerId || 'Not set'}</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SchoolAdminDetail; 